import React, { useEffect } from 'react';

/**
 * AudioUnlocker
 * - Unlocks audio playback on first user interaction (required by browser autoplay policies)
 * - After unlocking, ring tones (ring.mp3 / ringback.mp3) can play without user gesture on Callee side
 * - Safe no-op if already unlocked
 */
export default function AudioUnlocker() {
  useEffect(() => {
    let unlocked = false;

    const markUnlocked = () => {
      try {
        window.__audioUnlocked = true;
      } catch (_) {}
      unlocked = true;
      removeListeners();
    };

    const tryUnlock = async () => {
      if (unlocked || window.__audioUnlocked) return;
      try {
        // Resume WebAudio context if available
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          try {
            const ctx = new AC();
            await ctx.resume().catch(() => {});
            // close immediately to avoid leaking contexts
            try { if (ctx && ctx.state !== 'closed') { await ctx.close().catch(() => {}); } } catch (_) {}
          } catch (_) {}
        }

        // Attempt to play then immediately pause a short audio to satisfy autoplay gesture
        const ring = new Audio('/sounds/ring.mp3');
        ring.volume = 0.0; // silent pre-play to avoid audible blip
        ring.loop = false;

        await ring.play().catch(() => {});
        try { ring.pause(); ring.currentTime = 0; } catch (_) {}

        // Also warm-up ringback (some browsers treat different sources separately)
        const ringback = new Audio('/sounds/ringback.mp3');
        ringback.volume = 0.0;
        await ringback.play().catch(() => {});
        try { ringback.pause(); ringback.currentTime = 0; } catch (_) {}

        markUnlocked();
      } catch (_) {
        // ignore; will try again on next interaction
      }
    };

    const onInteraction = () => { tryUnlock(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') tryUnlock(); };

    function addListeners() {
      ['click', 'touchstart', 'keydown'].forEach((ev) => window.addEventListener(ev, onInteraction, { passive: true, capture: true }));
      document.addEventListener('visibilitychange', onVisibility, { passive: true });
    }
    function removeListeners() {
      ['click', 'touchstart', 'keydown'].forEach((ev) => window.removeEventListener(ev, onInteraction, { capture: true }));
      document.removeEventListener('visibilitychange', onVisibility);
    }

    addListeners();
    return () => removeListeners();
  }, []);

  return null;
}
